/**
 * stockReturn — quanto ainda está BAIXADO por um documento.
 *
 * A devolução ao estoque não pode usar a quantidade do documento: o prompt de
 * baixa deixa o usuário editar a quantidade (baixou 2 de um item de 3), e uma
 * mesma origem pode ter várias baixas parciais. Devolver o que está no
 * documento criaria estoque que nunca saiu — um saldo maior que a prateleira,
 * sem erro nenhum na tela.
 *
 * A conta certa é o SALDO LÍQUIDO dos movimentos daquela origem: as saídas
 * (`quote_sale`, delta negativo) mais as devoluções já feitas (`sale_return`,
 * delta positivo). Zero ou positivo → não há o que devolver, o que torna a
 * operação idempotente sem depender de marcador local.
 */

export interface StockMovementLike {
  product_id?: string | null;
  delta?: number | string | null;
  reason?: string | null;
}

export interface StockReturnLine {
  product_id: string;
  /** Quantidade POSITIVA a devolver. */
  quantity: number;
}

/**
 * Os motivos que compõem o saldo líquido baixado POR UM DOCUMENTO.
 *
 * Uma lista só para orçamento e OS de propósito: o recorte de verdade é o
 * `quote_id`/`service_order_id` da consulta, e um motivo que ficasse de fora de
 * uma das telas faria a mesma origem responder números diferentes conforme quem
 * pergunta. Motivo novo ligado a documento entra AQUI, não em cada consulta.
 */
export const DOCUMENT_STOCK_REASONS = [
  'quote_sale',
  'service_order_sale',
  'sale_return',
] as const;

/** Arredonda para 3 casas — a mesma precisão do saldo fracionário do módulo. */
function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

export function netDeductedByProduct(
  movements: readonly StockMovementLike[] | null | undefined,
): StockReturnLine[] {
  const net = new Map<string, number>();

  for (const movement of movements ?? []) {
    const productId = movement?.product_id ? String(movement.product_id) : '';
    if (!productId) continue;
    const delta = Number(movement?.delta ?? 0);
    if (!Number.isFinite(delta)) continue;
    net.set(productId, (net.get(productId) ?? 0) + delta);
  }

  const lines: StockReturnLine[] = [];
  for (const [product_id, sum] of net) {
    // `sum` é negativo enquanto sobra baixa; a devolução é o oposto dele.
    const quantity = round3(-sum);
    if (quantity > 0) lines.push({ product_id, quantity });
  }

  return lines.sort((a, b) => a.product_id.localeCompare(b.product_id));
}

/* ─── ACERTO POR QUANTIDADE ALTERADA (gatilho `quantity_changed`) ───────────
 *
 * Mesma fonte de verdade da devolução: os MOVIMENTOS. Não existe (e não
 * precisa existir) um "registro por item da baixa original" — o que saiu do
 * estoque por causa daquele documento é derivável somando os deltas por
 * produto. Comparando com o que o documento pede HOJE, a diferença é o acerto.
 *
 * ── O RECORTE, que é a parte não óbvia ──
 *
 * O acerto age SÓ sobre produtos que **ainda estão baixados** por este
 * documento (`deducted > 0`). As duas exclusões são deliberadas:
 *
 *  • **Produto sem movimento nenhum** (item novo acrescentado a um documento
 *    já baixado) é trabalho do `StockDeductionPrompt`, que existe exatamente
 *    para isso e já filtra por "produtos sem baixa". Os dois conjuntos são
 *    COMPLEMENTARES por construção — em nenhuma configuração as duas telas
 *    oferecem a mesma linha, que seria a pior falha possível aqui (baixa dupla
 *    oferecida em dois cartões).
 *  • **Produto já devolvido por inteiro** (`deducted === 0` depois de um
 *    `sale_return`) não volta a ser baixado por uma edição de quantidade: a
 *    devolução foi um ato deliberado do usuário, e re-baixar por causa de um
 *    número digitado seria o app desfazendo a decisão dele.
 *
 * Consequência para o caso "troquei o produto da linha, de A para B": A tem
 * movimento → é devolvido aqui; B não tem → aparece no prompt de baixa. A troca
 * é tratada por inteiro, em dois lugares distintos e visíveis, nunca pela
 * metade em silêncio.
 */

/** Um item de documento, do jeito mais frouxo que serve (a linha crua serve). */
export interface DocumentQuantityItem {
  product_id?: string | null;
  quantity?: number | string | null;
}

/**
 * O documento mudou DEPOIS da última movimentação de estoque?
 *
 * ⚠️ SEM ESTE PORTÃO O GATILHO NASCE ERRADO. O prompt de baixa deixa o usuário
 * baixar MENOS do que o documento pede (baixar 2 de um item de 3 é um gesto
 * deliberado e legítimo). Só olhando o saldo, esse caso é idêntico a "a
 * quantidade aumentou depois da baixa" — e o app passaria a cobrar
 * imediatamente "falta baixar 1", contradizendo o que o usuário acabou de
 * escolher.
 *
 * O que separa os dois é o TEMPO: acerto por quantidade só faz sentido se o
 * documento foi alterado depois do último movimento. É heurística, não prova —
 * salvar o documento por outro motivo (mudar o título, mudar o status) também
 * mexe no `updated_at`. Por isso o gatilho nasce desligado, o padrão é
 * PERGUNTAR e a dispensa vale para aquela divergência.
 *
 * Sem data de movimento (documento nunca baixado) não há o que acertar, e o
 * caller já não terá linhas; sem `updated_at` erramos para o lado de NÃO
 * mexer no estoque.
 */
export function documentChangedAfterMovements(
  documentUpdatedAt: string | null | undefined,
  movements: readonly { created_at?: string | null }[] | null | undefined,
): boolean {
  const updatedAt = documentUpdatedAt ? Date.parse(documentUpdatedAt) : NaN;
  if (!Number.isFinite(updatedAt)) return false;

  let last = -Infinity;
  for (const movement of movements ?? []) {
    const at = movement?.created_at ? Date.parse(movement.created_at) : NaN;
    if (Number.isFinite(at) && at > last) last = at;
  }
  if (last === -Infinity) return false;

  return updatedAt > last;
}

export interface StockAdjustmentLine {
  product_id: string;
  /** Delta ASSINADO a aplicar: positivo devolve, negativo baixa mais. */
  delta: number;
  /** Quanto ainda está baixado por este documento. */
  deducted: number;
  /** Quanto o documento pede hoje. */
  desired: number;
}

/**
 * Quanto cada produto pede HOJE no documento, consolidado por `product_id`.
 *
 * SEM filtro de rastreio de propósito: um produto que deixou de ser rastreado
 * continua contando o que a linha pede, então o acerto dá zero e nenhum
 * movimento é gerado. Filtrar aqui faria `desired` cair para 0 e o app tentaria
 * "devolver" para um produto sem rastreio — que é justamente o que a RPC
 * `apply_stock_movement` recusa, virando erro na tela por um item que ninguém
 * mexeu.
 */
export function desiredQuantityByProduct(
  items: readonly DocumentQuantityItem[] | null | undefined,
): Map<string, number> {
  const desired = new Map<string, number>();
  for (const item of items ?? []) {
    const productId = item?.product_id ? String(item.product_id) : '';
    if (!productId) continue;
    const quantity = Number(item?.quantity ?? 0);
    if (!Number.isFinite(quantity) || quantity <= 0) continue;
    desired.set(productId, (desired.get(productId) ?? 0) + quantity);
  }
  return desired;
}

/**
 * O acerto de estoque de um documento cuja quantidade mudou depois da baixa.
 *
 * `delta = baixado − pedido`: pedir mais do que saiu gera baixa complementar
 * (delta negativo), pedir menos — ou remover a linha, que é `desired = 0` —
 * gera devolução (delta positivo).
 */
export function quantityAdjustmentLines(
  movements: readonly StockMovementLike[] | null | undefined,
  items: readonly DocumentQuantityItem[] | null | undefined,
): StockAdjustmentLine[] {
  // `netDeductedByProduct` já devolve só quem tem saldo baixado > 0, que é
  // exatamente o recorte descrito acima.
  const deductedLines = netDeductedByProduct(movements);
  const desired = desiredQuantityByProduct(items);

  const lines: StockAdjustmentLine[] = [];
  for (const { product_id, quantity: deducted } of deductedLines) {
    const want = round3(desired.get(product_id) ?? 0);
    const delta = round3(deducted - want);
    if (delta === 0) continue;
    lines.push({ product_id, delta, deducted, desired: want });
  }

  return lines.sort((a, b) => a.product_id.localeCompare(b.product_id));
}
